import { useQuery } from '@tanstack/react-query';
import useAuthenticatedFetch from '@/domains/auth/hooks/authenticatedFetch';
import { ApiError } from '@/utils/ApiError';

import { z } from 'zod';
import { useAuthContext } from '@/domains/auth/authContext';
import { useAccount } from '../../components/AccountAccess/AccountContext';
import { useEffect, useState } from 'react';

const capabilitiesResponseSchema = z.object({
  customerId: z.string(),
  timestamp: z.string(),
  capabilities: z.object({
    featureFlags: z.array(z.string()).default([]),
    beehive: z.object({ allowed: z.boolean(), reason: z.string().optional() }),
    withdraw: z.object({ allowed: z.boolean(), reason: z.string().optional() }),
    plan: z.object({ allowed: z.boolean(), reason: z.string().optional() }),
    tracker: z.object({ allowed: z.boolean(), reason: z.string().optional() }),
    pensionAnalytics: z.object({
      allowed: z.boolean(),
      reason: z.string().optional(),
    }),
    transactionHistory: z.object({
      allowed: z.boolean(),
      reason: z.string().optional(),
    }),
    contributions: z.object({
      allowed: z.boolean(),
      reason: z.enum(['AGE', 'TAX_INFO', 'PLAN_BLOCKED']).optional(),
    }),
  }),
});

export type CapabilitiesResponse = z.infer<typeof capabilitiesResponseSchema>;

export const useCapabilitiesQuery = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { isLoggedIn } = useAuthContext();
  const { userType } = useAccount();
  const isAccount = userType === 'account';
  const [enabled, setEnabled] = useState<boolean>(false);

  useEffect(() => {
    setEnabled(isLoggedIn && isAccount);
  }, [isLoggedIn, isAccount]);

  return useQuery<CapabilitiesResponse>({
    queryKey: ['capabilities'],
    enabled: enabled,
    queryFn: async () => {
      const res = await authenticatedFetch('/api/app-bootstrap/capabilities', {
        method: 'GET',
      });

      if (!res.ok) {
        throw new ApiError('Failed to fetch account capabilities', res.status);
      }
      const data = await res.json();
      return capabilitiesResponseSchema.parse(data);
    },
  });
};
